/* ================================================================
   rb-gallery.js  —  Real Botanicals product media gallery
   Handles: thumbnail nav, swipe, keyboard, variant re-filtering
   ================================================================ */
(function () {
  'use strict';

  /* ── Helpers ──────────────────────────────────────────── */
  function norm(v) {
    return String(v || '').toLowerCase().replace(/\s+/g, '').replace(/\./g, '');
  }

  function removeCountUnit(v) {
    return v.replace(/count$/, '').replace(/(tablets?|tabs?)$/, '');
  }

  /* ── Gallery class ────────────────────────────────────── */
  class RbGallery {
    constructor(el) {
      this.el       = el;
      this.viewer   = el.querySelector('[data-rb-viewer]');
      this.thumbsEl = el.querySelector('[data-rb-thumbs]');
      this.prevBtn  = el.querySelector('[data-rb-prev]');
      this.nextBtn  = el.querySelector('[data-rb-next]');
      this.counter  = el.querySelector('[data-rb-counter]');

      this.slides = Array.from(el.querySelectorAll('[data-rb-slide]'));
      this.thumbs = Array.from(el.querySelectorAll('[data-rb-thumb]'));

      this.filterEnabled = el.dataset.filterEnabled === 'true';
      this.strengthIdx   = parseInt(el.dataset.strengthIdx ?? '-1', 10);
      this.sizeIdx       = parseInt(el.dataset.sizeIdx     ?? '-1', 10);
      this.flavorIdx     = parseInt(el.dataset.flavorIdx   ?? '-1', 10);

      // Product data for variant lookups. Prefer the variant list this gallery
      // renders for its own product; fall back to the page-level .product-json
      // that sections/product.liquid emits, for galleries rendered before the
      // scoped list existed.
      this.productData = null;
      const localVariants = el.querySelector('[data-rb-variants]');
      if (localVariants) {
        try { this.productData = { variants: JSON.parse(localVariants.textContent) }; } catch (_) {}
      }
      if (!this.productData) {
        const ps = document.querySelector('.product-json');
        if (ps) { try { this.productData = JSON.parse(ps.textContent); } catch (_) {} }
      }

      this._touchStartX = 0;
      this._bindEvents();
      this._updateArrows();
      this._updateCounter();
      this._buildLightbox();

      // After page load: preload all gallery images (low priority so they don't
      // compete with the active slide) then preload full-size lightbox images.
      const onLoad = () => {
        this._preloadGalleryImages();
        this._preloadLightboxImages();
      };
      if (document.readyState === 'complete') {
        onLoad();
      } else {
        window.addEventListener('load', onLoad, { once: true });
      }
    }

    /* ── Event binding ────────────────────────────────── */
    _bindEvents() {
      // Thumbnail clicks
      this.thumbs.forEach(t => {
        t.addEventListener('click', () => this._goToMedia(t.dataset.mediaId));
      });

      // Arrow buttons
      this.prevBtn?.addEventListener('click', () => this._step(-1));
      this.nextBtn?.addEventListener('click', () => this._step(1));

      // Keyboard — arrows only when gallery itself (or child) is focused
      this.el.setAttribute('tabindex', '-1');
      this.el.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   { e.preventDefault(); this._step(-1); }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown')  { e.preventDefault(); this._step(1);  }
      });

      // Touch / swipe in the viewer area
      this.viewer.addEventListener('touchstart', e => {
        this._touchStartX = e.touches[0].clientX;
      }, { passive: true });

      this.viewer.addEventListener('touchend', e => {
        const dx = e.changedTouches[0].clientX - this._touchStartX;
        if (Math.abs(dx) > 40) this._step(dx < 0 ? 1 : -1);
      }, { passive: true });

      // Zoom buttons → lightbox
      this.el.querySelectorAll('[data-rb-zoom]').forEach(btn => {
        btn.addEventListener('click', e => {
          e.stopPropagation();
          const slide = btn.closest('[data-rb-slide]');
          if (slide) this._openLightbox(slide.dataset.mediaId);
        });
      });

      /* ── Variant change hooks ─────────────────────── */
      if (!this.filterEnabled) return;

      // Custom events fired by variant picker JS
      ['variant:changed', 'product:variant-change', 'theme:variant:change'].forEach(evt => {
        document.addEventListener(evt, e => {
          // Prefer a full variant object; otherwise resolve the id to a real
          // variant so we have its option values (a bare {id} can't be filtered).
          let variant = e.detail?.variant;
          if (!variant && e.detail?.id != null) {
            variant = this.productData?.variants?.find(x => x.id === parseInt(e.detail.id, 10));
          }
          if (variant) this._applyFilter(variant);
        });
      });

      // Hidden id input (most theme variant pickers write here)
      const idInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (idInput) {
        const onIdChange = () => {
          const vid = parseInt(idInput.value, 10);
          const v   = this.productData?.variants?.find(x => x.id === vid);
          if (v) this._applyFilter(v);
        };
        idInput.addEventListener('change', onIdChange);
        new MutationObserver(onIdChange)
          .observe(idInput, { attributes: true, attributeFilter: ['value'] });
      }

      // Click on variant option buttons (belt-and-suspenders)
      document.addEventListener('click', e => {
        if (e.target.closest('[data-option-name],[data-option-index],.variant-button,.swatch,[name^="options["]')) {
          setTimeout(() => {
            const inp = document.querySelector('form[action*="/cart/add"] input[name="id"]');
            if (!inp) return;
            const vid = parseInt(inp.value, 10);
            const v   = this.productData?.variants?.find(x => x.id === vid);
            if (v) this._applyFilter(v);
          }, 0);
        }
      });
    }

    /* ── Filter helpers ───────────────────────────────── */
    _getStrength(variant) {
      if (this.strengthIdx < 0) return '';
      return norm(variant['option' + (this.strengthIdx + 1)] || '');
    }

    _getSize(variant) {
      if (this.sizeIdx < 0) return '';
      return removeCountUnit(norm(variant['option' + (this.sizeIdx + 1)] || '').replace(/-/g, ''));
    }

    _getFlavor(variant) {
      if (this.flavorIdx < 0) return '';
      return norm(variant['option' + (this.flavorIdx + 1)] || '').replace(/-/g, '');
    }

    _applyFilter(variant) {
      const strength = this._getStrength(variant);
      const size     = this._getSize(variant);
      const flavor   = this._getFlavor(variant);

      // Mirrors the Liquid visibility logic — all checks are independent:
      //   strength check skipped when product has no strength option or slide is tagged 'all'
      //   size check skipped when product has no size option or slide has no size tag (#_10ct style)
      //   flavor check skipped when product has no flavor option or slide has no flavor tag
      const allowed = new Set();

      this.slides.forEach(s => {
        const sStr = (s.dataset.filterStrength || 'all').toLowerCase();
        const sSz  = (s.dataset.filterSize     || '').toLowerCase().replace(/-/g, '');
        const sFl  = (s.dataset.filterFlavor   || '').toLowerCase().replace(/-/g, '');

        const strOk = !strength || sStr === 'all' || sStr === '' || sStr === strength;
        const szOk  = !size     || !sSz           || sSz === size;
        const flOk  = !flavor   || !sFl           || sFl === flavor;

        if (strOk && szOk && flOk) allowed.add(s.dataset.mediaId);
      });

      // Update slides
      this.slides.forEach(s => {
        const show = allowed.has(s.dataset.mediaId);
        s.hidden = !show;
        if (!show) s.classList.remove('rb-gallery__slide--active');
      });

      // Update thumbs
      this.thumbs.forEach(t => {
        const show = allowed.has(t.dataset.mediaId);
        t.hidden = !show;
        if (!show) t.classList.remove('rb-gallery__thumb--active');
      });

      // Activate first visible slide
      const first = this.slides.find(s => !s.hidden);
      if (first) this._goToMedia(first.dataset.mediaId, false);

      this._preloadLightboxImages();
    }

    /* ── Navigation ───────────────────────────────────── */
    _visibleSlides() { return this.slides.filter(s => !s.hidden); }

    _activeSlide() {
      return this.slides.find(s =>
        s.classList.contains('rb-gallery__slide--active') && !s.hidden
      );
    }

    _goToMedia(mediaId, pauseOthers = true) {
      if (pauseOthers) this._pauseAllMedia();

      // Deactivate everything
      this.slides.forEach(s => {
        s.classList.remove('rb-gallery__slide--active');
        s.setAttribute('aria-hidden', 'true');
      });
      this.thumbs.forEach(t => {
        t.classList.remove('rb-gallery__thumb--active');
        t.setAttribute('aria-pressed', 'false');
      });

      // Activate target slide
      const slide = this.slides.find(s => s.dataset.mediaId === mediaId);
      if (slide) {
        slide.classList.add('rb-gallery__slide--active');
        slide.setAttribute('aria-hidden', 'false');
      }

      // Activate matching thumb and bring it into view inside the thumb rail
      const thumb = this.thumbs.find(t => t.dataset.mediaId === mediaId);
      if (thumb) {
        thumb.classList.add('rb-gallery__thumb--active');
        thumb.setAttribute('aria-pressed', 'true');
        this._revealThumb(thumb);
      }

      this._updateArrows();
      this._updateCounter();
    }

    /* Scroll the thumbnail rail so `thumb` is visible, without ever moving the
       page. scrollIntoView() walks every scrollable ancestor up to the document,
       so it yanked the whole window down whenever a variant change re-filtered
       the gallery -- including the filter that runs from the variant picker's
       own cascade on page load. The rail is overflow-x:auto in the default row
       layout and overflow-y:auto when thumbnails sit to the left, so only the
       axis that actually scrolls is touched. */
    _revealThumb(thumb) {
      const rail = this.thumbsEl;
      if (!rail) return;

      const t = thumb.getBoundingClientRect();
      const r = rail.getBoundingClientRect();
      let dx = 0;
      let dy = 0;

      if (rail.scrollHeight > rail.clientHeight) {
        if (t.top < r.top)            dy = t.top - r.top;
        else if (t.bottom > r.bottom) dy = t.bottom - r.bottom;
      }

      if (rail.scrollWidth > rail.clientWidth) {
        if (t.left < r.left)          dx = t.left - r.left;
        else if (t.right > r.right)   dx = t.right - r.right;
      }

      if (dx || dy) rail.scrollBy({ left: dx, top: dy, behavior: 'smooth' });
    }

    _step(dir) {
      const visible = this._visibleSlides();
      if (visible.length < 2) return;
      const cur = this._activeSlide();
      const idx = visible.indexOf(cur);
      const next = visible[(idx + dir + visible.length) % visible.length];
      this._goToMedia(next.dataset.mediaId);
    }

    /* ── UI helpers ───────────────────────────────────── */
    _updateArrows() {
      if (!this.prevBtn) return;
      const show = this._visibleSlides().length > 1;
      this.prevBtn.hidden = !show;
      this.nextBtn.hidden = !show;
    }

    _updateCounter() {
      if (!this.counter) return;
      const visible = this._visibleSlides();
      const active  = this._activeSlide();
      const idx     = visible.indexOf(active) + 1;
      this.counter.textContent = idx + ' / ' + visible.length;
      this.counter.hidden = visible.length <= 1;
    }

    _pauseAllMedia() {
      this.slides.forEach(s => {
        s.querySelectorAll('video').forEach(v => { try { v.pause(); } catch (_) {} });
        s.querySelectorAll('iframe').forEach(iframe => {
          try {
            iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            iframe.contentWindow.postMessage('{"method":"pause"}', '*');
          } catch (_) {}
        });
      });
    }

    /* ── Preloading ───────────────────────────────────── */
    _preloadGalleryImages() {
      this.slides.forEach(s => {
        if (s.dataset.mediaType === 'image') {
          const img = s.querySelector('img');
          if (!img || img.complete) return;
          img.fetchPriority = 'low';
          img.loading = 'eager';
        } else if (s.dataset.mediaType === 'video') {
          const video = s.querySelector('video[preload="none"]');
          if (video) video.preload = 'metadata';
        }
      });
    }

    _preloadLightboxImages() {
      this.slides
        .filter(s => !s.hidden && s.dataset.mediaType === 'image')
        .forEach(s => {
          const src = s.querySelector('[data-rb-zoom]')?.dataset.fullSrc;
          if (src) new Image().src = src;
        });
    }

    _buildLightbox() {
      const lb = document.createElement('div');
      lb.className = 'rb-lightbox';
      lb.hidden = true;
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.setAttribute('aria-label', 'Image lightbox');
      lb.setAttribute('tabindex', '-1');

      lb.innerHTML = `
        <div class="rb-lightbox__wrap">
        <div class="rb-lightbox__top">
          <div class="rb-lightbox__top-left">
            <button class="rb-lightbox__btn" type="button" data-lb-zoom-out aria-label="Zoom out">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="10" cy="10" r="6" stroke="currentColor" stroke-width="2"/>
                <path d="M20 20l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M7 10h6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <button class="rb-lightbox__btn" type="button" data-lb-zoom-in aria-label="Zoom in">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="10" cy="10" r="6" stroke="currentColor" stroke-width="2"/>
                <path d="M20 20l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M10 7v6M7 10h6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <span class="rb-lightbox__counter" data-lb-counter aria-live="polite"></span>
          <button class="rb-lightbox__btn" type="button" data-lb-close aria-label="Close lightbox">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="square" d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="rb-lightbox__stage" data-lb-stage>
          <button class="rb-lightbox__nav rb-lightbox__nav--prev" type="button" data-lb-prev aria-label="Previous image">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/></svg>
          </button>
          <img class="rb-lightbox__img" data-lb-img src="" alt="" draggable="false">
          <button class="rb-lightbox__nav rb-lightbox__nav--next" type="button" data-lb-next aria-label="Next image">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/></svg>
          </button>
        </div>
        </div>
      `;

      document.body.appendChild(lb);
      this.lb        = lb;
      this.lbImg     = lb.querySelector('[data-lb-img]');
      this.lbStage   = lb.querySelector('[data-lb-stage]');
      this.lbCounter = lb.querySelector('[data-lb-counter]');
      this.lbPrevBtn = lb.querySelector('[data-lb-prev]');
      this.lbNextBtn = lb.querySelector('[data-lb-next]');

      this._lbScale     = 1;
      this._lbTx        = 0;
      this._lbTy        = 0;
      this._lbSlides    = [];
      this._lbIdx       = 0;
      this._lbDragMoved = false;

      lb.querySelector('[data-lb-close]').addEventListener('click', () => this._closeLightbox());
      lb.querySelector('[data-lb-zoom-in]').addEventListener('click', () => this._lbZoom(1.5));
      lb.querySelector('[data-lb-zoom-out]').addEventListener('click', () => this._lbZoom(1 / 1.5));
      this.lbPrevBtn.addEventListener('click', () => this._lbStep(-1));
      this.lbNextBtn.addEventListener('click', () => this._lbStep(1));

      // Single-click on image: zoom in to click point, or reset if already zoomed
      this.lbImg.addEventListener('click', e => {
        if (this._lbDragMoved) { this._lbDragMoved = false; return; }
        if (this._lbScale > 1) {
          this._lbSetZoom(1);
        } else {
          this._lbZoomToPoint(2.5, e.clientX, e.clientY);
        }
      });

      // Close on click on the backdrop — covers both the stage and the flex
      // empty space outside rb-lightbox__wrap (which lands on this.lb itself)
      this.lbStage.addEventListener('click', e => {
        if (this._lbDragMoved) { this._lbDragMoved = false; return; }
        if (e.target === this.lbStage) this._closeLightbox();
      });
      this.lb.addEventListener('click', e => {
        if (e.target === this.lb) this._closeLightbox();
      });

      document.addEventListener('keydown', e => {
        if (this.lb.hidden) return;
        switch (e.key) {
          case 'Escape':      this._closeLightbox();   break;
          case 'ArrowLeft':   this._lbStep(-1);        break;
          case 'ArrowRight':  this._lbStep(1);         break;
          case '+': case '=': this._lbZoom(1.5);       break;
          case '-':           this._lbZoom(1 / 1.5);   break;
        }
      });

      // Mouse wheel zoom
      this.lbStage.addEventListener('wheel', e => {
        e.preventDefault();
        this._lbZoom(e.deltaY < 0 ? 1.12 : 1 / 1.12);
      }, { passive: false });


      this._bindLbDrag();
      this._bindLbPinch();
      this._bindLbSwipe();
    }

    _openLightbox(mediaId) {
      this._lbSlides = this.slides
        .filter(s => !s.hidden && s.dataset.mediaType === 'image')
        .map(s => ({
          mediaId: s.dataset.mediaId,
          src:     s.querySelector('[data-rb-zoom]')?.dataset.fullSrc || s.querySelector('img')?.src || '',
          alt:     s.querySelector('img')?.alt || ''
        }));

      this._lbIdx = Math.max(0, this._lbSlides.findIndex(s => s.mediaId === mediaId));
      this._lbSetZoom(1, false);
      this._lbShowCurrent();
      this.lb.hidden = false;
      document.body.style.overflow = 'hidden';
      this.lb.focus();
    }

    _closeLightbox() {
      this.lb.hidden = true;
      document.body.style.overflow = '';
    }

    _lbShowCurrent() {
      const slide = this._lbSlides[this._lbIdx];
      if (!slide) return;
      this.lbImg.src = slide.src;
      this.lbImg.alt = slide.alt;
      const total = this._lbSlides.length;
      this.lbCounter.textContent = total > 1 ? `${this._lbIdx + 1} / ${total}` : '';
      this.lbPrevBtn.hidden = total <= 1;
      this.lbNextBtn.hidden = total <= 1;
    }

    _lbStep(dir) {
      const n = this._lbSlides.length;
      if (n <= 1) return;
      this._lbIdx = (this._lbIdx + dir + n) % n;
      this._lbSetZoom(1, false);
      this._lbShowCurrent();
    }

    _lbZoom(factor) {
      this._lbSetZoom(this._lbScale * factor);
    }

    _lbZoomToPoint(scale, cx, cy) {
      const rect = this.lbImg.getBoundingClientRect();
      const imgCx = rect.left + rect.width / 2;
      const imgCy = rect.top  + rect.height / 2;
      const newScale = Math.min(Math.max(scale, 1), 6);
      this._lbTx += (cx - imgCx) * (1 - newScale / this._lbScale);
      this._lbTy += (cy - imgCy) * (1 - newScale / this._lbScale);
      this._lbScale = newScale;
      this._lbApplyTransform();
      this.lbStage.classList.toggle('is-zoomed', this._lbScale > 1);
    }

    _lbSetZoom(scale, animated = true) {
      this._lbScale = Math.min(Math.max(scale, 1), 6);
      if (this._lbScale <= 1) { this._lbTx = 0; this._lbTy = 0; }
      if (!animated) this.lbImg.classList.add('no-transition');
      this._lbApplyTransform();
      if (!animated) requestAnimationFrame(() => this.lbImg.classList.remove('no-transition'));
      this.lbStage.classList.toggle('is-zoomed', this._lbScale > 1);
    }

    _lbApplyTransform() {
      this.lbImg.style.transform = `translate(${this._lbTx}px, ${this._lbTy}px) scale(${this._lbScale})`;
    }

    _bindLbDrag() {
      let dragging = false, startX, startY, startTx, startTy;

      this.lbStage.addEventListener('mousedown', e => {
        if (this._lbScale <= 1) return;
        if (e.target === this.lbPrevBtn || e.target === this.lbNextBtn) return;
        dragging = true;
        this._lbDragMoved = false;
        startX = e.clientX; startY = e.clientY;
        startTx = this._lbTx; startTy = this._lbTy;
        this.lbStage.classList.add('is-dragging');
        this.lbImg.classList.add('no-transition');
        e.preventDefault();
      });

      window.addEventListener('mousemove', e => {
        if (!dragging) return;
        if (Math.abs(e.clientX - startX) > 3 || Math.abs(e.clientY - startY) > 3) {
          this._lbDragMoved = true;
        }
        this._lbTx = startTx + (e.clientX - startX);
        this._lbTy = startTy + (e.clientY - startY);
        this._lbApplyTransform();
      });

      window.addEventListener('mouseup', () => {
        if (!dragging) return;
        dragging = false;
        this.lbStage.classList.remove('is-dragging');
        this.lbImg.classList.remove('no-transition');
      });
    }

    _bindLbPinch() {
      let lastDist = 0, lastMidX = 0, lastMidY = 0;

      this.lbStage.addEventListener('touchstart', e => {
        if (e.touches.length === 2) {
          lastDist = Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY
          );
          lastMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          lastMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
          e.preventDefault();
        }
      }, { passive: false });

      this.lbStage.addEventListener('touchmove', e => {
        if (e.touches.length !== 2) return;
        e.preventDefault();
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

        if (lastDist > 0) {
          const newScale = Math.min(Math.max(this._lbScale * (dist / lastDist), 1), 6);
          const rect  = this.lbImg.getBoundingClientRect();
          const imgCx = rect.left + rect.width  / 2;
          const imgCy = rect.top  + rect.height / 2;
          this._lbTx += (midX - imgCx) * (1 - newScale / this._lbScale) + (midX - lastMidX);
          this._lbTy += (midY - imgCy) * (1 - newScale / this._lbScale) + (midY - lastMidY);
          this._lbScale = newScale;
          this._lbApplyTransform();
          this.lbStage.classList.toggle('is-zoomed', this._lbScale > 1);
        }

        lastDist = dist; lastMidX = midX; lastMidY = midY;
      }, { passive: false });

      this.lbStage.addEventListener('touchend', e => {
        if (e.touches.length < 2) {
          lastDist = 0;
          if (this._lbScale < 1.05) this._lbSetZoom(1);
        }
      });
    }

    _bindLbSwipe() {
      let startX = 0, startY = 0;

      this.lbStage.addEventListener('touchstart', e => {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      this.lbStage.addEventListener('touchend', e => {
        if (this._lbScale > 1) return;
        if (e.changedTouches.length !== 1) return;
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this._lbStep(dx < 0 ? 1 : -1);
        }
      }, { passive: true });
    }
  }

  /* ── Bootstrap ────────────────────────────────────────── */
  function initGalleries() {
    document.querySelectorAll('[data-rb-gallery]').forEach(el => {
      if (!el._rbGallery) el._rbGallery = new RbGallery(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGalleries);
  } else {
    initGalleries();
  }
})();
